import React, { useState, useMemo } from 'react';
import { Button, Checkbox } from '@telus-uds/components-web';
import { ModalFooter, Td, Th, Tr, customStyles, TabContainer, TabBtn } from './css';
import Modal from '../../../design/shared/modal';
import { getLocale } from '../../../misc';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import Filter from './filter';
import { formatCurrency } from '../../../utils/currencyFormatter';
import Pagination from '../../../design/shared/table/pagination';
import TableForCouponCodeOffer from './table';
import {
  prepareCouponData,
  getMissingValidationOffer,
  isOfferForTsbPlan
} from './utils';
import ValidateLoader from '../../../UI/validateLoader';

const couponCodeOffer = ({
  data = {},
  setData,
  couponPage,
  setCouponPage,
  setSelectedCouponData,
  selectedCouponData = {},
  setFilters,
  filters,
  isLoading,
  limit = 5,
  isTSBModal,
  setSearchByOfferCode,
  isButtonDisabled = false,
  setIsFormTouched,
  setShowDownloadQuoteFooter,
  error: fieldError,
  isVulcanFlow,
  isFibreModal,
  isCopperModal,
  isInternetBackupSelected,
  selectedTsbPlan,
  isFibreEastRestricted
}) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [error, setError] = useState('');
  // T005 [US1]: active tab state; default is Plans
  // T010 [US2]: TSB modal shows only the Other Offers tab, so default to it; all other flows start on Plans
  const [activeTab, setActiveTab] = useState(isTSBModal ? 'Other Offers' : 'Plans');
  const isStickyFooter = true;

  const locale = getLocale();
  const {
    vulcanWebform: {
      _vulOffer,
      offerCode,
      offerDetails,
      valueCoupon,
      frequency,
      reedemOffer,
      noRecordsFound,
      enterValidationCode,
      _validationCode,
      _add,
      monthlyChargesColumn,
      oneTimeChargeColumn,
      tabPlans,
      tabOtherOffers,
      tabAddOns,
      noOffersAvailable
    }
  } = locale === 'en-US' ? EN : FR;

  const showModal = e => {
    e.preventDefault();
    setModalVisible(true);
    // `selectedCouponData.data` can be missing (rather than an empty array)
    // if it was ever reset to a bare object elsewhere — falling back to []
    // keeps `selectedRows` an array so `disabledOfferCodes`'s forEach below
    // doesn't crash ("Cannot read properties of undefined (reading
    // 'forEach')") on reopening the modal.
    setSelectedRows(
      Array.isArray(selectedCouponData.data) ? selectedCouponData.data : []
    );
  };

  const onCloseModal = () => {
    setModalVisible(false);
    setSelectedRows([]);
    setData({ orders: [], count: 0 });
  };

  /** Store validation code in the selected row (only used when Coupon Based Discount = Yes). */
  const setValidationCodeForOffer = (offerId, value) => {
    setSelectedRows(prev =>
      prev.map(r =>
        String(r.id) === String(offerId)
          ? { ...r, enteredValidationCode: value } : r
      )
    );
    setError('');
  };

  /**
   * Offer codes that must be disabled in the list because they are not stackable
   * with any currently selected offer. When e.g. Offer A is selected and A has
   * offerCodesNotStackable: ['B'], then B is in this set and the B row is greyed out
   * and its checkbox disabled. When A is deselected, B is removed from this set
   * and becomes selectable again.
   */
  const disabledOfferCodes = useMemo(() => {
    const set = new Set();
    selectedRows.forEach(row => {
      (row.offerCodesNotStackable || []).forEach(code => set.add(code));
    });
    return set;
  }, [selectedRows]);

  /**
   * Returns true if the two offers are configured as non-stackable with each other
   * (either offer lists the other in its offerCodesNotStackable array).
   */
  const isNotStackableWith = (offerA, offerB) => {
    const aCodes = offerA.offerCodesNotStackable || [];
    const bCodes = offerB.offerCodesNotStackable || [];
    return aCodes.includes(offerB.offerCode) || bCodes.includes(offerA.offerCode);
  };

  /**
   * Handles checkbox change for an offer row.
   * - On check: if the offer is disabled (not stackable with current selection), do nothing.
   *   Otherwise remove any already-selected offers that are not stackable with this one,
   *   then add this offer (so the selected list never contains conflicting offers).
   * - On uncheck: remove this offer from the selection (other offers may become selectable again).
   */
  const handleCheck = (row, checked) => {
    if (checked) {
      // Do not allow selecting an offer that is greyed out (not stackable with current selection).
      if (disabledOfferCodes.has(row.offerCode)) {
        return;
      }
      // Before selecting another offer, ensure any already-selected offer with mandatory validation code has it filled.
      const missingValidation = getMissingValidationOffer(selectedRows, locale);
      if (missingValidation) {
        setError(missingValidation.message);
        return;
      }
      // Remove any currently selected offers that conflict with the one we're adding.
      // E.g. user had B selected, then selects A (A and B not stackable) → B is removed, A is added.
      const withoutConflicts = selectedRows.filter(
        r => !isNotStackableWith(r, row)
      );
      const newSelection = [...withoutConflicts, row];

      if (withoutConflicts.length > 0) {
        const hasSameTerm = withoutConflicts.some(
          field => field.term && field.term.includes(row.term)
        );
        if (hasSameTerm) {
          setError(`You already select ${selectedRows[0].term} offer`);
          return;
        }
      }
      setError('');
      setSelectedRows(newSelection);
      return;
    }
    setError('');
    setSelectedRows(prev =>
      prev.filter(r => String(r.id) !== String(row.id))
    );
  };

  // T018 [US3]: Partition offers by offerType; null/undefined offerType → Plans tab default
  // T022 [US4]: When isTSBModal, exclude offers with null/empty tsbPlan; optionally filter by selectedTsbPlan
  const filteredOrders = useMemo(() => {
    const orders = (data.orders || []).filter(offer => {
      // Tab routing by offerType
      const type = offer.offerType || 'Plans';
      if (activeTab === 'Plans' && type !== 'Plans') return false;
      if (activeTab === 'Other Offers' && type !== 'Other Offers') return false;
      if (activeTab === 'Add Ons' && type !== 'Add Ons') return false;
      // T022: TSB plan safety filter — exclude null-tsbPlan TSB offers on the client side
      // FR-006b: match on canonical, locale-independent plan tokens. The selected plan
      // value comes from the form field as a localized label (e.g. 'Surveillance' in FR),
      // while offer.tsbPlan is canonical English (e.g. ['Monitor']). Normalize both sides
      // so the same offers match in English and French (FR-006c, SC-011).
      if (isTSBModal && !isOfferForTsbPlan(offer, selectedTsbPlan)) return false;
      return true;
    });
    return orders;
  }, [data.orders, activeTab, isTSBModal, selectedTsbPlan]);

  return (
    <>
      <div style={{ marginRight: '20px', display: 'inline-block' }}>
        <Button
          type="button"
          onClick={showModal}
          id="redeemOfferData"
          name="redeemOfferData"
          variant={{ priority: 'low' }}
          disabled={isButtonDisabled}
        >
          {isTSBModal ? reedemOffer : _vulOffer}
        </Button>
      </div>
      {fieldError && <p style={{ color: 'red', marginRight: '40px', marginTop: '10px' }}>{fieldError.text}</p>}
      <TableForCouponCodeOffer
        selectedCouponData={selectedCouponData}
        setSelectedCouponData={setSelectedCouponData}
        setIsFormTouched={setIsFormTouched}
        setShowDownloadQuoteFooter={setShowDownloadQuoteFooter}
      />
      <Modal
        open={isModalVisible}
        setOpen={onCloseModal}
        customStyles={customStyles}
        header={_vulOffer}
        isStickyFooter={isStickyFooter}
        minWidth="1280px"
      >
        <div style={{ paddingTop: '0.5rem' }}>
        {/* T012 [US2]: pass activeTab so filter can lock Term/Bundled on Other Offers tab */}
        <Filter
          setFiltersData={setFilters}
          setSearchByOfferCode={setSearchByOfferCode}
          setSelectedRows={setSelectedRows}
          selectedCouponData={selectedCouponData}
          setData={setData}
          isLoading={isLoading}
          isTSBModal={isTSBModal}
          isVulcanFlow={isVulcanFlow}
          isFibreModal={isFibreModal}
          isCopperModal={isCopperModal}
          isInternetBackupSelected={isInternetBackupSelected}
          activeTab={activeTab}
          isFibreEastRestricted={isFibreEastRestricted}
        />
        {/* T005/T010/T011 [US1/US2]: Tab bar — rendered below the search/filter row
             - Other Offers: always visible for all products
             - Plans: hidden for TSB (TSB shows Other Offers only)
             - Add Ons: hidden for TSB and for Vulcan fibre/copper flow */}
        <TabContainer role="tablist">
          {!isTSBModal && (
            <TabBtn
              type="button"
              active={activeTab === 'Plans'}
              role="tab"
              aria-selected={activeTab === 'Plans'}
              onClick={() => setActiveTab('Plans')}
            >
              {tabPlans}
            </TabBtn>
          )}
          <TabBtn
            type="button"
            active={activeTab === 'Other Offers'}
            role="tab"
            aria-selected={activeTab === 'Other Offers'}
            onClick={() => setActiveTab('Other Offers')}
          >
            {tabOtherOffers}
          </TabBtn>
          {!isTSBModal && !(isVulcanFlow && (isFibreModal || isCopperModal)) && (
            <TabBtn
              type="button"
              active={activeTab === 'Add Ons'}
              role="tab"
              aria-selected={activeTab === 'Add Ons'}
              onClick={() => setActiveTab('Add Ons')}
            >
              {tabAddOns}
            </TabBtn>
          )}
        </TabContainer>
        <div
          style={{
            width: '100%',
            maxWidth: '1200px',
            overflow: 'auto',
            margin: '0 auto'
          }}
        >
          {(() => {
            if (isLoading) {
              return (
                <ValidateLoader
                  message="Loading records, please wait…"
                  isFormStep
                />
              );
            }

            // T013 [US2]: No API results at all → original no-records message
            if (data.orders.length === 0) {
              return (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem 0' }}>
                  <p style={{ margin: 0, fontSize: '1rem' }}>{noRecordsFound}</p>
                </div>
              );
            }

            // T013 [US2]: API has results but current tab is empty → per-tab empty state
            if (filteredOrders.length === 0) {
              return (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <h2>{noOffersAvailable}</h2>
                </div>
              );
            }

            return (
              // T007 [US1]: Updated column headers — Monthly Charges / One-Time Charges
              <table style={{ width: '99%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ height: '60px' }}>
                    <Th />
                    <Th>{offerCode}</Th>
                    <Th>{_validationCode}</Th>
                    <Th>{offerDetails}</Th>
                    <Th>{monthlyChargesColumn}</Th>
                    <Th>{oneTimeChargeColumn}</Th>
                  </tr>
                </thead>
                <tbody>
                  {/* T018 [US3]: render filteredOrders (partitioned by offerType + TSB plan) */}
                  {filteredOrders.map((field) => {
                    const rowSpan = field.offerDetails.length;
                    const isRowDisabled = disabledOfferCodes.has(field.offerCode);
                    // T008 [US1]: per-group totals for Plans tab
                    const groupMonthly = field.offerDetails.reduce(
                      (sum, d) => (d.frequency === 'monthly' ? sum + parseFloat(d.value || 0) : sum), 0
                    );
                    const groupOneTime = field.offerDetails.reduce(
                      (sum, d) => (d.frequency === 'oneTime' ? sum + parseFloat(d.value || 0) : sum), 0
                    );
                    return (
                      <React.Fragment key={field.id}>
                        {field.offerDetails.map((detail, detailIndex) => (
                          <Tr
                            key={`${field.id}-${detailIndex}`}
                            border={detailIndex === 0}
                            style={isRowDisabled ? { opacity: 0.5, backgroundColor: '#f5f5f5' } : undefined}
                          >
                            {detailIndex === 0 && (
                              <>
                                <Td rowSpan={rowSpan}>
                                  <Checkbox
                                    checked={selectedRows.some(r => r.id === field.id)}
                                    onChange={checked => handleCheck(field, checked)}
                                    inactive={isRowDisabled}
                                  />
                                </Td>
                                <Td rowSpan={rowSpan}>{field.offerCode}</Td>
                                <Td rowSpan={rowSpan}>
                                  {(() => {
                                    const isMandatory = field.validationCode === 'Yes';
                                    // Show the code input whenever it's optionally supported OR
                                    // mandatory — checking couponBasedDiscount alone left mandatory
                                    // offers with no way to enter a code when the two flags were
                                    // out of sync, permanently stuck on the "code required" error.
                                    const isCouponBased = field.couponBasedDiscount === 'Yes' || isMandatory;
                                    const isSelected = selectedRows.some(r => String(r.id) === String(field.id));
                                    if (!isCouponBased || !isSelected) return '–';
                                    const selectedRow = selectedRows.find(r => String(r.id) === String(field.id));
                                    const val = selectedRow && selectedRow.enteredValidationCode != null
                                      ? selectedRow.enteredValidationCode : '';
                                    return (
                                      <input
                                        type="text"
                                        className="frg-input-field color-light"
                                        style={{ width: '100%', minWidth: '100px' }}
                                        placeholder={isMandatory ? `${enterValidationCode} *` : enterValidationCode}
                                        value={val}
                                        onChange={e => setValidationCodeForOffer(field.id, e.target.value)}
                                      />
                                    );
                                  })()}
                                </Td>
                              </>
                            )}
                            {/* T007 [US1]: Monthly/One-Time split instead of Value/Frequency */}
                            <Td>{locale === 'en-US' ? detail.offerDetailsEn : detail.offerDetailsFr}</Td>
                            <Td>{detail.frequency === 'monthly' ? formatCurrency(detail.value) : formatCurrency(0)}</Td>
                            <Td>{detail.frequency === 'oneTime' ? formatCurrency(detail.value) : formatCurrency(0)}</Td>
                          </Tr>
                        ))}
                        {/* T008 [US1]: Per-offer-code group total row in Plans tab only — bold per FR-003 */}
                        {activeTab === 'Plans' && (
                          <tr key={`${field.id}-total`} style={{ backgroundColor: '#f0f4f8' }}>
                            <Td bold colSpan={4} style={{ textAlign: 'right' }}>Total</Td>
                            <Td bold>{formatCurrency(groupMonthly)}</Td>
                            <Td bold>{formatCurrency(groupOneTime)}</Td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
          {filteredOrders.length > 0 && (
            <Pagination
              currentPage={parseInt(couponPage || 1, 10)}
              setSrtPage={setCouponPage}
              step={limit} // items per page
              count={data.count} // total number of items
            />
          )}
        </div>
        </div>
        <ModalFooter>
          {error && (
            <p style={{ color: 'red', marginRight: '40px' }}>{error}</p>
          )}
          <Button
            type="button"
            variant={{ priority: 'high' }}
            onClick={() => {
              const missingValidation = getMissingValidationOffer(selectedRows, locale);
              if (missingValidation) {
                setError(missingValidation.message);
                return;
              }
              setSelectedCouponData(prepareCouponData(selectedRows, filters));
              setIsFormTouched(true);
              setShowDownloadQuoteFooter(false);
              setSelectedRows([]);
              setModalVisible(false);
            }}
            disabled={selectedRows.length === 0 || data.orders.length === 0}
          >
            {_add}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default couponCodeOffer;
